import moment from "moment/moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  fetchSmsBillingOverview,
  fetchSmsNotifications,
  normaliseSmsNotificationsResponse,
  normaliseSmsNotificationSummary,
  readFirstDefined,
  smsNotificationActionsAvailable,
  SMS_NOTIFICATION_POLL_INTERVAL,
  SMS_NOTIFICATIONS_UPDATED_EVENT,
  SMS_NOTIFICATION_TYPE_LABELS,
} from "../../pages/SmsBilling/AutomationTemplates/automationTemplateApi";
import "./header-notifications.css";
import GalleryNotifications from './GalleryNotifications';
import useGallerySummary from '../../pages/SocialGalleryV2/useGallerySummary';

const NOTIFICATION_LIMIT = 6;

const TYPE_ICONS = {
  customer_reply: "bx bx-message-rounded-dots",
  reminder: "bx bx-alarm",
  automation: "bx bx-bolt-circle",
  delivery_failed: "bx bx-error-circle",
  delivery_success: "bx bx-check-circle",
  delivery_report: "bx bx-envelope",
  credit_warning: "bx bx-credit-card",
  system: "bx bx-info-circle",
};

const formatUnreadCount = (count) => {
  const value = Number(count || 0);

  if (value > 99) {
    return "99+";
  }

  return value > 0 ? value : "";
};

const formatRelativeTime = (date) => {
  if (!date) {
    return "";
  }

  const parsedDate = moment(date);

  return parsedDate.isValid() ? parsedDate.fromNow() : "";
};

const getNotificationRoute = (notification) => {
  if (notification.type === "customer_reply") {
    return notification.eventId
      ? `/conversions/view/${notification.eventId}`
      : "/conversions/conversations";
  }

  if (notification.type === "automation" && notification.flowId) {
    return `/conversions/automation-flows/${notification.flowId}`;
  }

  if (notification.type === "credit_warning") {
    return "/conversions";
  }

  if (notification.type === "reminder") {
    return "/conversions/notifications";
  }

  return "/conversions/notifications";
};

const getUnreadCountFromResponse = (summaryResponse, notificationsResponse) => {
  const summary = normaliseSmsNotificationSummary(summaryResponse?.data);
  const summaryCount = summary.unreadNotifications;

  if (summaryCount !== null && summaryCount !== undefined) {
    return Number(summaryCount) || 0;
  }

  const notifications = normaliseSmsNotificationsResponse(notificationsResponse?.data);
  const source = notificationsResponse?.data?.data || notificationsResponse?.data || {};
  const directCount = readFirstDefined(source, ["unread_count", "unreadCount"]);

  if (directCount !== null && directCount !== undefined) {
    return Number(directCount) || 0;
  }

  return notifications.data.reduce(
    (total, item) => total + (Number(item.unreadCount) || (item.unread ? 1 : 0)),
    0
  );
};

const HeaderNotifications = ({ enabled, onUnreadCountChange }) => {
  const gallerySummary = useGallerySummary();
  const history = useHistory();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  const mountedRef = useRef(false);

  const loadNotifications = useCallback(async () => {
    if (!enabled || loadingRef.current) {
      return;
    }

    loadingRef.current = true;
    setLoading(true);

    try {
      const params = { page: 1, per_page: NOTIFICATION_LIMIT };
      const [summaryResult, notificationsResult] = await Promise.allSettled([
        fetchSmsBillingOverview(),
        fetchSmsNotifications(params),
      ]);
      const nextNotifications =
        notificationsResult.status === "fulfilled"
          ? normaliseSmsNotificationsResponse(notificationsResult.value.data).data.slice(0, NOTIFICATION_LIMIT)
          : [];

      if (!mountedRef.current) {
        return;
      }

      const nextUnreadCount = getUnreadCountFromResponse(
        summaryResult.status === "fulfilled" ? summaryResult.value : null,
        notificationsResult.status === "fulfilled" ? notificationsResult.value : null
      );
      setNotifications(nextNotifications);
      setUnreadCount(nextUnreadCount);
      onUnreadCountChange?.(nextUnreadCount);
    } catch (error) {
      // Fail silently so notification polling never breaks the venue header.
    } finally {
      loadingRef.current = false;

      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [enabled, onUnreadCountChange]);

  useEffect(() => {
    if (!enabled) {
      onUnreadCountChange?.(0);
      return undefined;
    }

    mountedRef.current = true;
    loadNotifications();
    const pollId = window.setInterval(loadNotifications, SMS_NOTIFICATION_POLL_INTERVAL);
    window.addEventListener(SMS_NOTIFICATIONS_UPDATED_EVENT, loadNotifications);

    return () => {
      mountedRef.current = false;
      window.clearInterval(pollId);
      window.removeEventListener(SMS_NOTIFICATIONS_UPDATED_EVENT, loadNotifications);
    };
  }, [enabled, loadNotifications, onUnreadCountChange]);

  const handleNotificationClick = (notification) => {
    history.push(getNotificationRoute(notification));
  };

  if (!enabled && !gallerySummary) {
    return null;
  }

  const notificationCount = (enabled ? unreadCount : 0) + (gallerySummary?.total || 0);

  return (
    <li className="nav-item dropdown dropdown-lg header-notification-dropdown">
      <Link
        className="nav-link dropdown-toggle dropdown-toggle-nocaret header-notification-toggle"
        to="#"
        data-toggle="dropdown"
        aria-label="Notifications"
        title="Unread SMS notifications, new Gallery enquiries and active offers"
      >
        <i className="bx bx-bell" />
        {notificationCount > 0 ? (
          <span className="badge badge-danger header-notification-count">
            {formatUnreadCount(notificationCount)}
          </span>
        ) : null}
      </Link>
      <div className="dropdown-menu dropdown-menu-right header-notification-menu">
        <div className="header-notification-title">
          <strong>Notifications</strong>
          {enabled && <button
            type="button"
            className="btn btn-link btn-sm p-0"
            disabled={!smsNotificationActionsAvailable}
          >
            Mark SMS read
          </button>}
        </div>

        <div className="header-notification-list">
          <GalleryNotifications summary={gallerySummary} />
          {enabled && <><div className="header-sms-section-title">SMS notifications</div>
          {loading && !notifications.length ? (
            <div className="header-notification-state">
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            </div>
          ) : notifications.length ? (
            notifications.map((notification) => (
              <button
                type="button"
                key={notification.id}
                className={`header-notification-item ${notification.unread ? "unread" : ""}`}
                onClick={() => handleNotificationClick(notification)}
              >
                <span className="header-notification-icon">
                  <i className={TYPE_ICONS[notification.type] || TYPE_ICONS.system} />
                </span>
                <span className="header-notification-copy">
                  <span className="header-notification-row">
                    <strong>{notification.title || SMS_NOTIFICATION_TYPE_LABELS.system}</strong>
                    {notification.unread ? <span className="header-notification-dot" /> : null}
                  </span>
                  <span className="header-notification-message">
                    {notification.message || "No message available."}
                  </span>
                  <span className="header-notification-time">
                    {formatRelativeTime(notification.timestamp)}
                  </span>
                </span>
              </button>
            ))
          ) : (
            <div className="header-notification-state text-muted">No new notifications.</div>
          )}</>}
        </div>
        {enabled && <Link to="/conversions/notifications" className="dropdown-item header-notification-view-all">
          View All SMS Notifications
        </Link>}
      </div>
    </li>
  );
};

export default HeaderNotifications;
